import i18n from '@/i18n'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { Workspace, WorkspaceBranding, Entitlements, FeatureFlag, EntitlementLimitKey, EntitlementLimits, PlansApiResponse, PlanTier, WorkspaceTier } from '@/types'
import { workspaceService, browserTimeZone, type CreateWorkspaceData, type UpdateWorkspaceData } from '@/services/workspaces.service'
import { initTracking } from '@/utils/metaPixel'

const LAST_WORKSPACE_COOKIE = 'pj_last_workspace'

function readLastWorkspaceId(): string | null {
  try {
    const prefix = `${LAST_WORKSPACE_COOKIE}=`
    const cookie = document.cookie.split(';').map(value => value.trim())
      .find(value => value.startsWith(prefix))
    return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null
  } catch {
    return null
  }
}

function rememberWorkspace(id: Workspace['id']): void {
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${LAST_WORKSPACE_COOKIE}=${encodeURIComponent(String(id))}; Path=/; Max-Age=31536000; SameSite=Lax${secure}`
  } catch {
    // A blocked cookie must not prevent opening a workspace.
  }
}

export const useWorkspaceStore = defineStore('workspace', () => {
  // State
  const currentWorkspace = ref<Workspace | null>(null)
  const workspaces = ref<Workspace[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const tokens = ref<number | null>(null)
  const isLoadingTokens = ref(false)
  // Plans payload cache — populated after the first /workspaces/plans call.
  // Used by `getMinTierForFeature` to power tier-targeted "Upgrade to X"
  // copy on the UpgradeModal. Null until a page sets it;
  // getMinTierForFeature degrades gracefully when null.
  const availablePlans = ref<PlansApiResponse | null>(null)

  // Computed
  const currentWorkspaceId = computed(() => currentWorkspace.value?.id || null)
  const hasWorkspaces = computed(() => workspaces.value.length > 0)
  const workspaceLanguage = computed(() => currentWorkspace.value?.language || 'en')
  const creditsRemaining = computed(() => tokens.value ?? currentWorkspace.value?.tokens ?? 0)
  const entitlements = computed<Entitlements | null>(() => currentWorkspace.value?.entitlements ?? null)

  function getFeatureAccess(flag: FeatureFlag): 'full' | 'limited' | 'gated' | 'hidden' {
    const features = entitlements.value?.features
    if (!features) return 'hidden'

    // Mirror the backend for both root and nested paths. `enabled` can be a
    // boolean (pages.assets) or an access level; a truthy 'hidden'/'gated'
    // string must never be promoted to full access.
    let current: any = features
    for (const part of flag.split('.')) {
      if (!current || typeof current !== 'object') return 'hidden'
      current = current[part]
    }
    if (current && typeof current === 'object') current = current.enabled
    if (typeof current === 'string') return current as 'full' | 'limited' | 'gated' | 'hidden'
    if (current === true) return 'full'
    return 'hidden'
  }

  function hasFeature(flag: FeatureFlag): boolean {
    const access = getFeatureAccess(flag)
    return access === 'full' || access === 'limited'
  }

  // Match hasCapability on the API during the capability cutover. An
  // older custom/legacy agreement without these keys retains its access.
  function getCapabilityAccess(flag: FeatureFlag): 'full' | 'limited' | 'gated' | 'hidden' {
    if (['campaigns', 'email.sequences', 'inbox', 'inbox.aiResponses', 'catalog.checkout', 'settings.domains'].includes(flag) && entitlements.value) {
      let value: any = entitlements.value.features
      for (const part of flag.split('.')) value = value && typeof value === 'object' ? value[part] : undefined
      const tier = entitlements.value.tier || currentWorkspace.value?.workspaceTier
      if (value === undefined && tier && ['custom', 'pro', 'business', 'launch_monthly', 'launch_annual'].includes(tier)) return 'full'
    }
    return getFeatureAccess(flag)
  }

  // Limits lookup — mirrors `getLimit(workspace, key)` on the backend.
  // Returns null when entitlements aren't loaded or the key is unset.
  function getLimit<K extends EntitlementLimitKey>(key: K): EntitlementLimits[K] | null {
    const limits = entitlements.value?.limits
    if (!limits) return null
    const value = (limits as any)[key]
    return value === undefined ? null : value
  }

  // Walks the plans payload and returns the cheapest tier where `flag`
  // resolves to `full`/`limited`. Powers tier-targeted upgrade copy
  // ("Upgrade to Business to unlock the catalog").
  //
  // Returns null when:
  //   - availablePlans hasn't been loaded yet (page hasn't called setPlans),
  //   - the feature is already available on the current tier,
  //   - no tier unlocks it (truly hidden everywhere).
  //
  // The tier ordering is by monthly price ascending on the first sprawl
  // rung — matches how PlanCards renders the ladder and avoids hardcoding
  // tier-order in two places.
  function getMinTierForFeature(flag: FeatureFlag): { tier: WorkspaceTier; monthly: number } | null {
    const plans = availablePlans.value
    const tierMap = plans?.plans
    if (!tierMap) return null

    // Custom and legacy packages may not appear in the public plan catalog.
    // The workspace's resolved entitlement snapshot is the authoritative
    // answer for whether it already has the feature.
    const resolvedAccess = getCapabilityAccess(flag)
    if (resolvedAccess === 'full' || resolvedAccess === 'limited') return null

    function walkFeatures(features: Record<string, any>, path: string): string | null {
      const parts = path.split('.')
      let current: any = features
      for (const p of parts) {
        if (!current || typeof current !== 'object') return null
        current = current[p]
      }
      if (current && typeof current === 'object') current = current.enabled
      if (typeof current === 'string') return current
      if (current === true) return 'full'
      if (current === false) return 'hidden'
      return null
    }

    const candidates: Array<{ tier: WorkspaceTier; monthly: number }> = []
    for (const [tierName, node] of Object.entries(tierMap) as [string, PlanTier][]) {
      const access = walkFeatures(node.features || {}, flag)
      if (access !== 'full' && access !== 'limited') continue
      const monthly = node.tokenSprawls?.[0]?.monthly ?? 0
      candidates.push({ tier: tierName as WorkspaceTier, monthly })
    }

    if (candidates.length === 0) return null
    candidates.sort((a, b) => a.monthly - b.monthly)
    // Skip tiers the workspace is already on or above. If the current
    // tier already unlocks it, don't show an upgrade prompt.
    return candidates[0] ?? null
  }

  function setPlans(plans: PlansApiResponse | null): void {
    availablePlans.value = plans
  }

  // Eagerly fetches the plans payload so tier-targeted upgrade copy is
  // available everywhere (dashboard upgrade card, sidebar UpgradeModal,
  // header tier pill). Cheap (cached, no-op when already loaded),
  // country-scoped, never throws.
  async function ensurePlansLoaded(): Promise<void> {
    if (availablePlans.value) return
    const country = currentWorkspace.value?.country
    if (!country) return
    try {
      const res = await workspaceService.getPlans(country)
      availablePlans.value = res.data ?? null
    } catch (e) {
      // Plans are an enrichment, not load-bearing — degrade silently to
      // the generic upgrade copy if the call fails.
      console.warn('[workspace.ensurePlansLoaded] failed:', e)
    }
  }

  // Single-product catalog mode (LP-style: one CatalogEntry per page, no
  // cart, inline checkout). Backed by the `catalog.singleProduct` feature
  // flag — same predicate the backend uses (entitlementsService).
  // Centralised here so palette filters, page gates and catalog UI all read
  // one truth instead of recomputing `hasFeature('catalog.singleProduct')`
  // locally.
  const isSingleProductCatalog = computed(() => hasFeature('catalog.singleProduct'))

  // Actions
  function setCurrentWorkspace(workspace: Workspace | null): void {
    currentWorkspace.value = workspace
    // Initialize tracking pixels for this workspace's country
    if (workspace) {
      rememberWorkspace(workspace.id)
      initTracking()
      adoptBrowserTimeZone(workspace)
    }
  }

  // A workspace that never had a time zone chosen takes the browser zone of
  // the first owner or admin who opens it. Asked at most once per workspace
  // per page load; a failure leaves the default in place.
  const timeZoneAdoptAsked = new Set<string>()
  function adoptBrowserTimeZone(workspace: Workspace): void {
    const id = String(workspace.id)
    const role = workspace.WorkspaceMember?.role
    if (workspace.timezoneSetBy !== 'default' || (role !== 'owner' && role !== 'admin')) return
    if (timeZoneAdoptAsked.has(id)) return
    const zone = browserTimeZone()
    if (!zone) return
    timeZoneAdoptAsked.add(id)
    workspaceService.adoptTimeZone(id, zone).then(({ data }) => {
      const merge = (w: Workspace): Workspace => ({ ...w, timezone: data.timezone, timezoneSetBy: data.timezoneSetBy })
      const index = workspaces.value.findIndex(w => String(w.id) === id)
      if (index !== -1 && workspaces.value[index]) workspaces.value[index] = merge(workspaces.value[index])
      if (currentWorkspace.value && String(currentWorkspace.value.id) === id) {
        currentWorkspace.value = merge(currentWorkspace.value)
      }
    }).catch(() => {})
  }

  function patchWorkspaceBranding(
    workspaceId: string | number,
    patch: Partial<WorkspaceBranding>,
  ): void {
    const applyPatch = (workspace: Workspace): Workspace => ({
      ...workspace,
      branding: {
        ...(workspace.branding || {}),
        ...patch,
      } as WorkspaceBranding,
    })

    const index = workspaces.value.findIndex(workspace =>
      String(workspace.id) === String(workspaceId)
    )
    if (index !== -1 && workspaces.value[index]) {
      workspaces.value[index] = applyPatch(workspaces.value[index])
    }

    if (String(currentWorkspace.value?.id) === String(workspaceId) && currentWorkspace.value) {
      currentWorkspace.value = applyPatch(currentWorkspace.value)
    }
  }

  async function fetchWorkspaces(): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const response = await workspaceService.getAll()
      workspaces.value = response.data

      // Restore the browser's last workspace only when it is still available.
      if (!currentWorkspace.value && workspaces.value.length > 0) {
        const lastWorkspaceId = readLastWorkspaceId()
        const remembered = workspaces.value.find(workspace => String(workspace.id) === lastWorkspaceId)
        setCurrentWorkspace(remembered ?? workspaces.value[0] ?? null)
      }
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : i18n.global.t('stores.workspace.failedToFetchWorkspaces')
      error.value = errorMessage
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function fetchWorkspace(id: string): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      const response = await workspaceService.get(id)
      setCurrentWorkspace(response.data)
      // Also fetch tokens when fetching workspace
      fetchTokens(id).catch(() => {
        // Silently handle token fetch errors - tokens will show as 0
      })
      // Warm the plans cache so tier-targeted upgrade copy is available
      // before the user clicks anything. Non-blocking.
      ensurePlansLoaded().catch(() => {})
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : i18n.global.t('stores.workspace.failedToFetchWorkspace')
      error.value = errorMessage
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function fetchTokens(workspaceId: string): Promise<void> {
    isLoadingTokens.value = true
    try {
      const response = await workspaceService.getTokens(workspaceId)
      tokens.value = response.data.tokens
    } catch (e: unknown) {
      console.error('Failed to fetch tokens:', e)
      // Don't throw - tokens are a non-critical feature
    } finally {
      isLoadingTokens.value = false
    }
  }

  async function createWorkspace(data: CreateWorkspaceData): Promise<Workspace> {
    isLoading.value = true
    error.value = null

    try {
      const timezone = browserTimeZone()
      const response = await workspaceService.create(timezone ? { ...data, timezone } : data)
      const newWorkspace = response.data
      workspaces.value.push(newWorkspace)
      return newWorkspace
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : i18n.global.t('stores.workspace.failedToCreateWorkspace')
      error.value = errorMessage
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function updateWorkspace(id: string, data: UpdateWorkspaceData): Promise<Workspace> {
    isLoading.value = true
    error.value = null

    try {
      const response = await workspaceService.update(id, data)
      const updatedWorkspace = response.data

      // Merge updated fields into existing objects (preserve joined data like entitlements)
      const index = workspaces.value.findIndex(w => String(w.id) === String(id))
      if (index !== -1) {
        workspaces.value[index] = { ...workspaces.value[index], ...updatedWorkspace }
      }

      if (String(currentWorkspace.value?.id) === String(id)) {
        currentWorkspace.value = { ...currentWorkspace.value, ...updatedWorkspace }
      }

      return updatedWorkspace
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : i18n.global.t('stores.workspace.failedToUpdateWorkspace')
      error.value = errorMessage
      throw e
    } finally {
      isLoading.value = false
    }
  }

  async function retireWorkspace(id: string): Promise<void> {
    await workspaceService.retire(id)

    // Remove from local list
    workspaces.value = workspaces.value.filter(w => String(w.id) !== String(id))

    // If this was the current workspace, switch to another or clear
    if (String(currentWorkspace.value?.id) === String(id)) {
      if (workspaces.value.length > 0) {
        setCurrentWorkspace(workspaces.value[0] ?? null)
      } else {
        currentWorkspace.value = null
      }
    }
  }

  function clearWorkspace(): void {
    currentWorkspace.value = null
  }

  function clearAll(): void {
    currentWorkspace.value = null
    workspaces.value = []
    error.value = null
    tokens.value = null
  }

  return {
    // State
    currentWorkspace,
    workspaces,
    isLoading,
    error,
    tokens,
    isLoadingTokens,
    // Computed
    currentWorkspaceId,
    hasWorkspaces,
    workspaceLanguage,
    creditsRemaining,
    entitlements,
    getFeatureAccess,
    hasFeature,
    getCapabilityAccess,
    getLimit,
    getMinTierForFeature,
    setPlans,
    ensurePlansLoaded,
    availablePlans,
    isSingleProductCatalog,
    // Actions
    setCurrentWorkspace,
    patchWorkspaceBranding,
    fetchWorkspaces,
    fetchWorkspace,
    fetchTokens,
    createWorkspace,
    updateWorkspace,
    retireWorkspace,
    clearWorkspace,
    clearAll
  }
})
